import type * as bytescale from "@bytescale/sdk";
import { type ReactNode, forwardRef, useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { processImageFilePath } from "../process-file-path";
import { useBytescaleContext } from "./use-bytescale-context";

export type PrivateImgProps = Omit<
  React.ComponentPropsWithoutRef<"img">,
  "src"
> & {
  src: string | null | undefined;
  children?: ReactNode;
  "data-slot"?: string;
  options?: Omit<bytescale.UrlBuilderOptionsImage, "transformation">;
};

export function useLoadPrivateImage(
  src: string | null | undefined,
  options?: Omit<bytescale.UrlBuilderOptionsImage, "transformation">,
) {
  const [isAuthenticated, _setIsAuthenticated] = useState(true);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const context = useBytescaleContext();
  const source = context.accountId
    ? processImageFilePath(src, context.accountId, options)
    : undefined;

  useEffect(() => {
    const preLoadImage = (src: string) => {
      const img = new Image();
      img.src = src;
      img.onload = () => {
        // Removed Bytescale authentication for now, authentication is set to true by default
        // if (bytescale.AuthManager.isAuthSessionReady()) {
        //   setIsAuthenticated(true);
        // }
        setIsLoading(false);
      };

      img.onerror = () => {
        setIsError(true);
        setIsLoading(false);
      };
    };

    if (source) {
      preLoadImage(source);
    }

    // Removed Bytescale authentication for now, authenticated is set to true by default
    // const interval = setInterval(() => {
    //   console.log("interval auth");
    //   if (bytescale.AuthManager.isAuthSessionReady()) {
    //     setIsAuthenticated(true);
    //     setIsError(false);
    //     clearInterval(interval);
    //   }
    // }, 100);

    // return () => clearInterval(interval);
  }, [source]);

  function resetError() {
    setIsError(false);
  }

  return {
    isAuthenticated,
    isError,
    resetError,
    isLoading,
    source,
  };
}

export const PrivateImg = forwardRef<HTMLImageElement, PrivateImgProps>(
  function PrivateImg(
    { alt, children, className, src, options, ...props },
    ref,
  ) {
    const { isAuthenticated, isError, resetError, isLoading, source } =
      useLoadPrivateImage(src, options);

    if (!source) {
      return (
        children || (
          <div
            data-slot={props["data-slot"]}
            className={twMerge(
              "animate-pulse bg-secondary flex-shrink-0",
              className,
            )}
          />
        )
      );
    }

    if (isLoading || !isAuthenticated) {
      return (
        <div
          data-slot={props["data-slot"]}
          className={twMerge(
            "animate-pulse bg-secondary flex-shrink-0",
            className,
          )}
        />
      );
    }

    return !isError ? (
      <img
        ref={ref}
        {...props}
        src={source}
        alt={alt}
        onError={resetError}
        data-slot={props["data-slot"]}
        className={twMerge("flex-shrink-0", className)}
      />
    ) : (
      children || (
        <div
          data-slot={props["data-slot"]}
          className={twMerge(
            "animate-pulse bg-secondary flex-shrink-0",
            className,
          )}
        />
      )
    );
  },
);
